/**
 * Wreckage: armour, limbs and masonry knocked off a piece mid-battle.
 *
 * A shed part is detached from its piece into the scene, given an impulse, and
 * then integrated as a loose body — gravity, a bounce off the board, tumbling,
 * friction — until it settles. It rests where it lands for a few seconds, then
 * fades and is disposed of.
 *
 * Two rules govern this file, and both are easy to get wrong:
 *
 *   1. NEVER dispose a body's geometry unless we generated it ourselves.
 *      Geometry comes from the shared library in parts.js and the same
 *      BufferGeometry backs that part on every piece on the board; disposing
 *      one would blank the part everywhere. `ownsGeometry` marks the few we
 *      may free.
 *   2. ALWAYS clone a material before fading it. materials.js hands out cached
 *      instances shared across both armies, so writing `opacity` on the
 *      original would fade every piece using that colour.
 *
 * Bodies are ticked from Animator.update rather than from a timeline, so
 * wreckage keeps falling after the battle that produced it has resolved.
 */

import * as THREE from 'three';

const UP = new THREE.Vector3(0, 1, 0);

// Scratch objects, reused every frame to keep this allocation-free.
const _q = new THREE.Quaternion();
const _qDelta = new THREE.Quaternion();
const _qStep = new THREE.Quaternion();
const _e = new THREE.Euler();
const _axis = new THREE.Vector3();
const _vec = new THREE.Vector3();
const _box = new THREE.Box3();
const _tmpBox = new THREE.Box3();
const _mat = new THREE.Matrix4();
const _center = new THREE.Vector3();

/**
 * How a body behaves once it is loose. Uniform tumbling reads badly — a shield
 * spinning like a dice is the giveaway — so each shape gets its own character.
 */
const KINDS = {
  // Plates: shield halves, capes, pauldrons. Wobble like a dropped plate and
  // end up lying face-up.
  flat:    { restitution: 0.30, friction: 0.62, spinDamp: 0.35, gravity: 1.0, settle: true },
  // Helms, crowns, orbs, pommels. Skitter, then roll.
  compact: { restitution: 0.42, friction: 0.80, spinDamp: 0.70, gravity: 1.0, roll: true },
  // Masonry. One dull thud and it stops.
  heavy:   { restitution: 0.10, friction: 0.45, spinDamp: 0.45, gravity: 1.15 },
  // Snapped shafts and blades. Fall over onto their side.
  long:    { restitution: 0.20, friction: 0.70, spinDamp: 0.60, gravity: 1.0, topple: true }
};

/** The board surface, and how far past its edge a body may still land. */
const FLOOR_Y = 0;
const BOARD_EDGE = 4.4;
const KILL_Y = -3;

/** Returns a node's axis-aligned bounds in its own local space. */
function localBounds(node) {
  node.updateWorldMatrix(true, true);
  _mat.copy(node.matrixWorld).invert();
  _box.makeEmpty();
  node.traverse((o) => {
    if (!o.isMesh) return;
    if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
    _tmpBox.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld).applyMatrix4(_mat);
    _box.union(_tmpBox);
  });
  return _box;
}

export class DebrisField {
  constructor(scene, {
    max = 48, rest = 8, fade = 1.2, gravity = 9.2, onImpact = () => {}
  } = {}) {
    this.scene = scene;
    this.bodies = [];
    this.max = max;
    this.rest = rest;
    this.fade = fade;
    this.gravity = gravity;
    // Reports what landed, how hard and where, and lets the mixer decide how
    // it should sound — the field knows materials, not cues.
    this.onImpact = onImpact;
  }

  get count() {
    return this.bodies.length;
  }

  /**
   * Detaches a live child of a piece and turns it loose.
   *
   * `scene.attach` preserves the world transform, which bakes the piece's
   * scale chain into the node — so the part does not visibly change size at
   * the instant it comes off, and must not be rescaled afterwards. It also
   * reparents to the scene, so the wreckage survives the victim mesh being
   * removed at the end of the capture.
   */
  shed(node, opts = {}) {
    if (!node || !node.parent) return null;
    this.scene.attach(node);
    return this.adopt(node, opts);
  }

  /**
   * Turns an object we built ourselves loose at a given world transform.
   * Used for fragments that have no intact counterpart on the model, such as
   * masonry spalling off a golem.
   */
  spawn(node, worldMatrix, opts = {}) {
    if (worldMatrix) {
      node.matrix.copy(worldMatrix);
      node.matrix.decompose(node.position, node.quaternion, node.scale);
    }
    this.scene.add(node);
    return this.adopt(node, { ownsGeometry: true, ...opts });
  }

  /** Shared setup once a node is a scene child: physical state and materials. */
  adopt(node, opts = {}) {
    const kind = KINDS[opts.kind] ? opts.kind : 'compact';
    const profile = KINDS[kind];

    // Size drives the floor offset. The smallest extent is the right one for a
    // plate — it should come to rest lying down, not balanced on an edge.
    const local = localBounds(node);
    const size = local.getSize(_vec).clone();
    const scale = node.scale;
    const world = new THREE.Vector3(
      Math.abs(size.x * scale.x), Math.abs(size.y * scale.y), Math.abs(size.z * scale.z)
    );
    const extents = [world.x, world.y, world.z];
    const smallest = extents.indexOf(Math.min(...extents));
    const largest = extents.indexOf(Math.max(...extents));
    // Plates and shafts both end up lying down, so their resting height is
    // half their thinnest dimension. Lumps sit on their average radius.
    const liesDown = kind === 'flat' || kind === 'long';
    const radius = liesDown
      ? Math.max(0.012, extents[smallest] * 0.5)
      : Math.max(0.02, (world.x + world.y + world.z) / 6);

    // `radius` alone assumes the node's origin sits at its own geometric
    // centre — true for a part authored that way, false for a part whose
    // pivot is its joint (a lance gripped at the hand, a helm hung from the
    // head). Off-centre parts would then rest with their *origin* a radius
    // above the floor while their actual geometry floats or sinks. The centre
    // is recorded here, in the same local frame `radius` is measured in, and
    // carried forward through node.scale (frozen once a body is loose) so
    // `floorOffset` only has to rotate it, not rebuild it, every frame.
    const localCenter = local.getCenter(new THREE.Vector3());
    const centerOffset = new THREE.Vector3(
      localCenter.x * scale.x, localCenter.y * scale.y, localCenter.z * scale.z
    );

    const materials = [];
    node.traverse((o) => {
      if (!o.isMesh) return;
      // Rule 2: break the share with the material cache before we fade it.
      o.material = o.material.clone();
      // Set once, at birth — toggling `transparent` later changes render
      // order and makes the body pop as it starts to fade.
      o.material.transparent = true;
      o.material.opacity = 1;
      o.castShadow = world.x + world.y + world.z > 0.18;
      o.receiveShadow = false;
      materials.push(o.material);
    });

    const body = {
      node,
      kind,
      profile,
      materials,
      ownsGeometry: !!opts.ownsGeometry,
      vel: opts.velocity ? opts.velocity.clone() : new THREE.Vector3(),
      spin: opts.spin ? opts.spin.clone() : new THREE.Vector3(),
      radius,
      centerOffset,
      // The local axis normal to a plate, so `settle` knows what to lay flat.
      flatAxis: new THREE.Vector3(
        smallest === 0 ? 1 : 0, smallest === 1 ? 1 : 0, smallest === 2 ? 1 : 0
      ),
      // The local axis along a shaft's length, so `settleLong` knows what to
      // bring down to horizontal. The *largest* extent, not the smallest: a
      // rod's two thin cross-section axes are near-identical, so picking
      // between them is arbitrary, but its length is unambiguous.
      longAxis: new THREE.Vector3(
        largest === 0 ? 1 : 0, largest === 1 ? 1 : 0, largest === 2 ? 1 : 0
      ),
      material: opts.material ?? 'steel',
      restitution: opts.restitution ?? profile.restitution,
      friction: opts.friction ?? profile.friction,
      gravityScale: opts.gravityScale ?? profile.gravity,
      state: 'flying',
      quiet: 0,
      restTimer: opts.rest ?? this.rest,
      fadeTimer: opts.fade ?? this.fade,
      fadeFor: opts.fade ?? this.fade,
      // The rook's core keeps glowing on the board rather than fading out.
      permanent: !!opts.permanent
    };

    this.bodies.push(body);
    this.enforceCap();
    return body;
  }

  /** Force-retires the oldest settled body once the field is full. */
  enforceCap() {
    if (this.bodies.length <= this.max) return;
    const victim = this.bodies.find((b) => b.state === 'resting' && !b.permanent)
      ?? this.bodies.find((b) => b.state !== 'fading');
    if (victim) {
      victim.state = 'fading';
      victim.fadeTimer = Math.min(victim.fadeTimer, 0.35);
      victim.fadeFor = victim.fadeTimer;
    }
  }

  update(dt) {
    if (!this.bodies.length) return;

    for (let i = this.bodies.length - 1; i >= 0; i--) {
      const body = this.bodies[i];
      if (body.state === 'flying') {
        this.integrate(body, dt);
        if (body.state === 'dead') {
          this.dispose(body);
          this.bodies.splice(i, 1);
        }
      } else if (body.state === 'resting') {
        if (body.permanent) continue;
        body.restTimer -= dt;
        if (body.restTimer <= 0) body.state = 'fading';
      } else {
        // Every body is its own transparent draw call, and a shadow-caster is
        // re-rendered into the shadow map every frame. A body on its way out
        // has no business costing that.
        if (!body.unshadowed) {
          body.node.traverse((o) => { if (o.isMesh) o.castShadow = false; });
          body.unshadowed = true;
        }
        body.fadeTimer -= dt;
        const k = Math.max(0, body.fadeTimer / body.fadeFor);
        for (const m of body.materials) {
          m.opacity = k;
          m.depthWrite = k > 0.99;
        }
        // Sinking as it fades reads as the field clearing itself; a body
        // dissolving in place looks like a rendering fault.
        body.node.position.y -= 0.35 * dt;
        if (body.fadeTimer <= 0) {
          this.dispose(body);
          this.bodies.splice(i, 1);
        }
      }
    }
  }

  /**
   * How far below the node's origin its geometry currently extends.
   *
   * Treats the shape as a sphere of `radius` centred on the part's real
   * centre rather than on its origin, and rotates that centre with the body
   * — cheap, and enough to stop an off-centre part resting with its geometry
   * floating above the board or sunk into it, without pretending the physics
   * models the true silhouette. Recomputed every call rather than cached,
   * because `settleFlat` can rotate the body mid-frame.
   */
  floorOffset(body) {
    _center.copy(body.centerOffset).applyQuaternion(body.node.quaternion);
    return Math.max(0.012, body.radius - _center.y);
  }

  integrate(body, dt) {
    const { node, vel, spin, profile } = body;

    vel.y -= this.gravity * body.gravityScale * dt;
    node.position.addScaledVector(vel, dt);

    // Composed as a quaternion rather than Euler angles, or fast tumbles gimbal.
    _e.set(spin.x * dt, spin.y * dt, spin.z * dt);
    _q.setFromEuler(_e);
    node.quaternion.premultiply(_q);

    const overBoard = Math.abs(node.position.x) <= BOARD_EDGE
      && Math.abs(node.position.z) <= BOARD_EDGE;

    if (!overBoard) {
      // Knocked clean off the table: nothing here to land on. Marked rather
      // than spliced, so the caller's loop owns removal.
      if (node.position.y < KILL_Y) body.state = 'dead';
      return;
    }

    if (node.position.y <= FLOOR_Y + this.floorOffset(body) && vel.y < 0) {
      node.position.y = FLOOR_Y + this.floorOffset(body);
      vel.y = -vel.y * body.restitution;
      vel.x *= body.friction;
      vel.z *= body.friction;
      spin.multiplyScalar(profile.spinDamp);
      if (Math.abs(vel.y) < 0.5) vel.y = 0;

      // Every landing above a whisper is reported. Impacts arriving together
      // are merged by the mixer rather than discarded here, so a golem
      // shedding masonry sounds like one crash instead of nothing.
      // A settling body bounces many times, each smaller than the last, and
      // the last dozen are inaudible in a fight — so the threshold rises with
      // each report from the same body. The first landing is the loud one.
      const force = Math.abs(vel.x) + Math.abs(vel.z) + Math.abs(vel.y);
      body.hits = (body.hits ?? 0);
      if (force > 0.3 + body.hits * 0.35) {
        body.hits += 1;
        this.onImpact(body.material, force, node.position);
      }
      if (profile.settle) this.settleFlat(body, 0.35);
      if (profile.topple) this.settleLong(body, 0.35);
    }

    const speed = vel.length();

    // A dome that is sliding should visibly roll instead.
    if (profile.roll && node.position.y <= FLOOR_Y + this.floorOffset(body) + 0.01 && speed > 0.3) {
      _axis.crossVectors(UP, vel);
      if (_axis.lengthSq() > 1e-6) {
        _axis.normalize().multiplyScalar(speed / Math.max(body.radius, 0.03));
        spin.lerp(_axis, 0.25);
      }
    }

    if (profile.settle && speed < 0.6) this.settleFlat(body, dt * 7);
    // A shaft left standing on end settles onto its side, the same way a
    // plate settles face-up — a direct correction toward the target
    // orientation rather than a torque nudge, which could leave a shaft
    // frozen on end if it ran out of angular momentum before it fell.
    if (profile.topple && speed < 0.6) this.settleLong(body, dt * 7);

    const onFloor = node.position.y <= FLOOR_Y + this.floorOffset(body) + 0.01;
    if (onFloor && speed < 0.14 && spin.length() < 0.15) body.quiet += dt;
    else body.quiet = 0;

    if (body.quiet > 0.12) {
      node.position.y = FLOOR_Y + this.floorOffset(body);
      vel.set(0, 0, 0);
      spin.set(0, 0, 0);
      if (profile.settle) this.settleFlat(body, 1);
      if (profile.topple) this.settleLong(body, 1);
      body.state = 'resting';
      // The rest timer starts here, not at the shed, so a helm that rolls a
      // long way still gets its full time lying on the board.
      body.restTimer = this.rest;
    }
  }

  /**
   * Rotates a plate toward lying flat on the board. Whichever face is already
   * uppermost stays up, so a shield settles rather than flipping over.
   */
  settleFlat(body, factor) {
    _vec.copy(body.flatAxis).applyQuaternion(body.node.quaternion).normalize();
    _axis.set(0, _vec.y >= 0 ? 1 : -1, 0);
    _qDelta.setFromUnitVectors(_vec, _axis);
    _qStep.identity().slerp(_qDelta, Math.min(1, Math.max(0, factor)));
    body.node.quaternion.premultiply(_qStep);
  }

  /**
   * Rotates a shaft toward lying on its side. Unlike `settleFlat`, "flat" for
   * a rod is not one target vector but a whole horizontal plane of headings,
   * so the target is the current heading with its vertical component removed
   * — the shaft falls to whichever side it was already leaning, rather than
   * spinning to some canonical direction.
   */
  settleLong(body, factor) {
    _vec.copy(body.longAxis).applyQuaternion(body.node.quaternion).normalize();
    _axis.set(_vec.x, 0, _vec.z);
    // Standing dead upright leaves no heading to fall toward; any horizontal
    // direction is as good as another.
    if (_axis.lengthSq() < 1e-6) _axis.set(1, 0, 0);
    _axis.normalize();
    _qDelta.setFromUnitVectors(_vec, _axis);
    _qStep.identity().slerp(_qDelta, Math.min(1, Math.max(0, factor)));
    body.node.quaternion.premultiply(_qStep);
  }

  dispose(body) {
    this.scene.remove(body.node);
    for (const m of body.materials) m.dispose();
    if (body.ownsGeometry) {
      // Rule 1: only ever true for geometry this module created.
      body.node.traverse((o) => { if (o.isMesh) o.geometry.dispose(); });
    }
  }

  /** Drops everything — a new game, a reset, or leaving for the portal. */
  clear() {
    for (const body of this.bodies) this.dispose(body);
    this.bodies = [];
  }
}
